/**
 * AdminResetPasswordModal — lets an admin (org admin or super/ALL-org admin)
 * set a new password for any user they manage. Backed by
 * POST /api/admin-ops/users/:userId/reset-password, which also confirms the
 * target's email if it wasn't already and revokes their existing sessions.
 *
 * The admin types or generates a password, submits it, and the modal keeps
 * the value visible + copyable afterwards so the admin can pass it along.
 */

import { useEffect, useMemo, useState } from 'react';
import { KeyRound, Eye, EyeOff, RefreshCw, Copy, Check, X, Loader2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAdminResetPassword, useAdminUserAuthProviders } from '../api/extensions/useAdminOps';
import { notify } from '../utils/notifications';

/** Multi-colour Google "G" mark (same artwork as the sign-in buttons). */
function GoogleGlyph({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

interface AdminResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** user_profiles.id of the target user. */
  userId: string | null;
  userName?: string | null;
  userEmail?: string | null;
}

// Unambiguous alphabet — no 0/O, 1/l/I — plus a couple of symbols, so the
// generated password is easy to read aloud / copy without transcription slips
// while still being strong.
const PW_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
const PW_LENGTH = 16;

function generatePassword(): string {
  const bytes = new Uint32Array(PW_LENGTH);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < PW_LENGTH; i++) {
    out += PW_ALPHABET[bytes[i] % PW_ALPHABET.length];
  }
  return out;
}

export default function AdminResetPasswordModal({
  isOpen,
  onClose,
  userId,
  userName,
  userEmail,
}: AdminResetPasswordModalProps) {
  const resetPassword = useAdminResetPassword();
  const { data: providersData } = useAdminUserAuthProviders(isOpen ? userId : null);
  const providers = providersData?.providers ?? [];
  const isGoogleAccount = providers.includes('google');
  const hasPasswordAlready = providers.includes('email');

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [done, setDone] = useState<{ emailWasConfirmed: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fresh generated password each time the modal opens for a user.
  useEffect(() => {
    if (isOpen) {
      setPassword(generatePassword());
      setShowPassword(false);
      setCopied(false);
      setDone(null);
      setError(null);
    }
  }, [isOpen, userId]);

  // Lock background scroll while open (matches the other admin modals).
  useEffect(() => {
    if (!isOpen) return;
    const html = document.documentElement;
    html.style.setProperty('overflow-y', 'hidden', 'important');
    return () => {
      html.style.removeProperty('overflow-y');
    };
  }, [isOpen]);

  const tooShort = useMemo(() => password.length > 0 && password.length < 8, [password]);
  const displayName = userName?.trim() || userEmail || 'this user';

  if (!isOpen || !userId) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      notify.error('Could not copy to clipboard — select and copy it manually');
    }
  };

  const handleSubmit = async () => {
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    try {
      const result = await resetPassword.mutateAsync({ userId, newPassword: password });
      setDone({ emailWasConfirmed: !!result?.emailWasConfirmed });
      notify.success(
        result?.emailWasConfirmed
          ? `Password set and email confirmed for ${displayName}`
          : `Password reset for ${displayName}`,
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to reset password';
      setError(message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />
      <div className="relative z-50 w-full max-w-md rounded-lg border bg-card text-card-foreground shadow-xl">
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              <div>
                <h3 className="text-lg font-semibold">Reset password</h3>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {displayName}
                  {userEmail && userName ? ` · ${userEmail}` : ''}
                </p>
                {isGoogleAccount && (
                  <span className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    <GoogleGlyph />
                    {hasPasswordAlready ? 'Google + password account' : 'Google sign-in account'}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-accent"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {done ? (
            <>
              <div className="flex items-start gap-2 rounded-md border border-green-600/40 bg-green-600/10 px-3 py-2 text-sm text-green-500">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Password updated and existing sessions signed out.
                  {done.emailWasConfirmed && ' Their email address has also been confirmed.'}
                  {' '}Share the password below with {displayName} — it won't be shown again.
                </span>
              </div>
              <div>
                <label className="text-sm font-medium">New password</label>
                <div className="mt-1 flex items-center gap-2">
                  <Input readOnly value={password} type={showPassword ? 'text' : 'password'} className="font-mono" />
                  <Button type="button" variant="outline" size="icon" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button type="button" variant="outline" size="icon" onClick={handleCopy} aria-label="Copy password">
                    {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <Button type="button" onClick={onClose}>Done</Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-start gap-2 rounded-md border border-amber-600/40 bg-amber-600/10 px-3 py-2 text-sm text-amber-500">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  This sets a new password immediately, signs {displayName} out of all sessions, and
                  confirms their email if it isn't already. Make sure you have a way to give them the
                  new password.
                  {isGoogleAccount && !hasPasswordAlready && (
                    <> This account currently signs in with Google — setting a password just <em>adds</em> an
                    email + password login; their Google sign-in keeps working.</>
                  )}
                </span>
              </div>

              <div>
                <label htmlFor="admin-reset-password" className="text-sm font-medium">New password</label>
                <div className="mt-1 flex items-center gap-2">
                  <Input
                    id="admin-reset-password"
                    value={password}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="font-mono"
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <Button type="button" variant="outline" size="icon" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button type="button" variant="outline" size="icon" onClick={handleCopy} aria-label="Copy password">
                    {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                  <Button type="button" variant="outline" size="icon" onClick={() => { setPassword(generatePassword()); setCopied(false); }} aria-label="Generate a new password">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
                {tooShort && <p className="mt-1 text-sm text-destructive">Must be at least 8 characters.</p>}
                {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={onClose} disabled={resetPassword.isPending}>
                  Cancel
                </Button>
                <Button type="button" onClick={handleSubmit} disabled={resetPassword.isPending || password.length < 8}>
                  {resetPassword.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Set password
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
